/**
 * Canton identity derivation — the byte formats that must match the server exactly.
 *
 * Authoritative server implementation: `API-accounts/src/services/canton-identity.js`.
 * This file is its mirror image; any change here without a matching change there breaks
 * every signature and every party lookup.
 *
 *   fingerprint = hex( 0x12 0x20 || sha256( purpose_BE32(12) || rawPublicKey[32] ) )
 *   partyHint   = "nuxaris-user-" + first 12 chars of the account UUID, dashes stripped
 *   partyId     = `${partyHint}::${fingerprint}`
 *
 * The `1220` prefix is a multihash header (0x12 = sha2-256, 0x20 = 32 bytes long).
 * The party id therefore contains a *hash* of the public key, never the key itself.
 */

import { sha256 } from '@noble/hashes/sha256';
import { toHex, fromHex } from './bytes';
import { InvalidInputError, InvalidKeyError } from './errors';

/** Canton `HashPurpose` value used for public key fingerprints. */
export const CANTON_KEY_FINGERPRINT_PURPOSE = 12;

/** Multihash prefix: sha2-256 (0x12), 32 bytes (0x20). */
const MULTIHASH_SHA256_32 = Uint8Array.from([0x12, 0x20]);

/** Length of a raw Ed25519 key (public or private scalar), in bytes. */
export const ED25519_KEY_LENGTH = 32;

/**
 * Canton fingerprint of a raw 32-byte Ed25519 public key.
 * Returns 68 lowercase hex chars ("1220" + 64).
 */
export function cantonFingerprint(rawPublicKey: Uint8Array): string {
  if (!(rawPublicKey instanceof Uint8Array) || rawPublicKey.length !== ED25519_KEY_LENGTH) {
    throw new InvalidKeyError(
      `Ed25519 public key must be ${ED25519_KEY_LENGTH} raw bytes, got ${
        rawPublicKey ? rawPublicKey.length : 'nothing'
      }`,
    );
  }

  const preimage = new Uint8Array(4 + ED25519_KEY_LENGTH);
  // 4-byte big-endian hash purpose.
  preimage[0] = (CANTON_KEY_FINGERPRINT_PURPOSE >>> 24) & 0xff;
  preimage[1] = (CANTON_KEY_FINGERPRINT_PURPOSE >>> 16) & 0xff;
  preimage[2] = (CANTON_KEY_FINGERPRINT_PURPOSE >>> 8) & 0xff;
  preimage[3] = CANTON_KEY_FINGERPRINT_PURPOSE & 0xff;
  preimage.set(rawPublicKey, 4);

  const digest = sha256(preimage);
  const multihash = new Uint8Array(MULTIHASH_SHA256_32.length + digest.length);
  multihash.set(MULTIHASH_SHA256_32, 0);
  multihash.set(digest, MULTIHASH_SHA256_32.length);
  return toHex(multihash);
}

/** Same as {@link cantonFingerprint} but taking the public key as hex. */
export function fingerprintFromPublicKeyHex(publicKeyHex: string): string {
  return cantonFingerprint(fromHex(publicKeyHex, ED25519_KEY_LENGTH));
}

/**
 * Party hint for an account: `nuxaris-user-` + the first 12 characters of the account
 * UUID with dashes removed. Mirrors `partyHintFor` server-side, character for character.
 */
export function partyHintFor(accountId: string): string {
  if (typeof accountId !== 'string' || accountId.trim().length === 0) {
    throw new InvalidInputError('accountId is required');
  }
  return `nuxaris-user-${accountId.replace(/-/g, '').substring(0, 12)}`;
}

/** Full Canton party id: `<hint>::<fingerprint>`. */
export function partyIdFor(accountId: string, publicKeyHex: string): string {
  return `${partyHintFor(accountId)}::${fingerprintFromPublicKeyHex(publicKeyHex)}`;
}
